

















































































































import { defineComponent, PropType, computed, ref } from '@nuxtjs/composition-api';
import dayjs from 'dayjs';
import { uniq } from 'lodash-es';
import AppPrice from '~/components/partials/App/AppPrice.vue';
import AppRectButton from '~/components/partials/App/AppRectButton.vue';
import ProductLabel from '~/components/partials/Product/ProductLabel.vue';
import { useStore } from '~/composables/useStore';
import { nonNullable } from '~/utils';
import { checkLotteryStatus } from '~/utils/checkLotteryStatus';
import { getProductPrice, getSpecialPricePromotion } from '~/utils/modules/products';
import { ProductVariantShow } from '~/types/modules/product';
import { PromotionsName, LotteryApplicationType } from '~/enums';

export default defineComponent({
  components: {
    AppPrice,
    AppRectButton,
    ProductLabel,
  },
  props: {
    productVariants: {
      type: Array as PropType<ProductVariantShow[]>,
      required: true,
    },
  },
  emits: {
    addToCart: (productVariantId: number) => typeof productVariantId === 'number',
    onClickLottery: (lotteryId?: number) => typeof lotteryId === 'number',
  },
  setup(props, { emit }) {
    const store = useStore();
    const appliedLotteries = ref(store.state.modules.lottery.appliedLotteries);
    const formattedProductVariants = computed(() =>
      props.productVariants
        .map((variant) => {
          return {
            productId: variant.id,
            vintageCode: variant.vintage_code,
            priceSetting: getProductPrice(variant.price_settings),
            isNowOnSale: variant.now_on_sale_flag,
            hasStock: variant.stock_count > 0,
            earliestShippingDate: variant.earliest_shipping_date,
            isD1Target: variant.d1_icon_display_flag,
            lottery: {
              applicationStatus: variant.raffle_item
                ? checkLotteryStatus(
                    variant.raffle_item.raffle.application_start_time,
                    variant.raffle_item.raffle.application_end_time,
                    variant.raffle_item.raffle.purchase_start_time,
                    variant.raffle_item.raffle.raffle_control_end_time
                  )
                : LotteryApplicationType.OTHER,
              id: variant.raffle_item?.raffle.id,
            },
          };
        })
        .filter(nonNullable)
    );

    const isVisaMember = computed(() => {
      if (!store.state.auth.authenticated) {
        return false;
      }

      return store.getters['user/info/isVisaMember'];
    });

    const isCartProcessing = computed(() => store.state.modules.cart.isCartProcessing);

    const onClickLottery = async (lotteryId?: number) => {
      emit('onClickLottery', lotteryId);
    };

    const saleLabel = (productVariantId: number): string => {
      const variant = findVariant(productVariantId);

      // 特価が設定されていない子商品のセールラベルは除く
      if (!getSpecialPricePromotion(variant?.price_settings)) {
        return '';
      }

      return variant?.price_settings.sale_info || '';
    };

    const sameProductPromotionLabels = (productVariantId: number) =>
      uniq(findVariant(productVariantId)?.single_product_promotions.map(({ name }) => name) || []);

    const multiProductPromotionLabels = (productVariantId: number) =>
      uniq(findVariant(productVariantId)?.multi_product_promotions.map(({ name }) => name) || []);

    const hasVISAPromotion = (productVariantId: number) => {
      return (
        findVariant(productVariantId)?.price_settings.promotions?.some(
          ({ name }) => name === PromotionsName.VISA_HAPPY_WINE_PRICE
        ) && isVisaMember.value
      );
    };

    const findVariant = (productVariantId: number) => {
      return props.productVariants.find((variant) => variant.id === productVariantId);
    };

    const addToCart = (productVariantId?: number) => {
      emit('addToCart', productVariantId);
      /* GTMにタグ設置のイベントを発火 */
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'tlb_dmp_cart',
        tlb_dmp: { user_id: store.state.user.info.userBase?.uuid, item_id: productVariantId },
      });
    };

    const checkLotteryApplicationStatus = (productVariantId: number) => {
      return !!appliedLotteries.value.find((appliedLottery) =>
        appliedLottery.raffle_items.map((raffleItem) => raffleItem.product_variant.id).includes(productVariantId)
      );
    };

    return {
      saleLabel,
      isCartProcessing,
      sameProductPromotionLabels,
      multiProductPromotionLabels,
      hasVISAPromotion,
      findVariant,
      formattedProductVariants,
      addToCart,
      isVisaMember,
      dayjs,
      onClickLottery,
      checkLotteryApplicationStatus,
      LotteryApplicationType,
    };
  },
});
